import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, MapPin, Clock, Calendar, Filter, Building2, 
  Phone, Mail, Globe, ChevronRight, Users
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import PageHeader from '../components/PageHeader';
import { Loader } from '../components/ui/loader';
import EmptyState from '../components/EmptyState';
import { API_URL } from '../utils/api';

const DAYS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ClassesPage() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');

  useEffect(() => {
    fetchClasses();
    fetchCities();
  }, []);

  const fetchClasses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/classes`);
      if (res.ok) {
        const data = await res.json();
        setClasses(data);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/classes/cities/list`);
      if (res.ok) {
        const data = await res.json();
        setCities(data);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const filteredClasses = classes.filter(cls => {
    const matchesSearch = !searchTerm || 
      cls.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cls.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cls.venue_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDay = !selectedDay || cls.day_of_week === selectedDay;
    const matchesLevel = !selectedLevel || cls.level === selectedLevel;
    const matchesCity = !selectedCity || cls.city === selectedCity;
    
    return matchesSearch && matchesDay && matchesLevel && matchesCity;
  });

  // Group by day of week
  const groupedClasses = DAYS_ORDER.reduce((acc, day) => {
    const dayClasses = filteredClasses.filter(c => c.day_of_week === day);
    if (dayClasses.length > 0) {
      acc[day] = dayClasses.sort((a, b) => a.start_time.localeCompare(b.start_time));
    }
    return acc;
  }, {});

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-100 text-green-800 border-green-200';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Advanced': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDay('');
    setSelectedLevel('');
    setSelectedCity('');
  };

  const hasFilters = searchTerm || selectedDay || selectedLevel || selectedCity;

  if (loading) {
    return <Loader fullScreen label="Loading classes" />;
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      {/* Hero Section */}
      <PageHeader
        title="Line Dance Classes"
        subtitle="Find line dance classes across the UK. From beginners to advanced, there's something for everyone."
      />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl p-4 md:p-6 mb-8 border border-stone-200">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-semibold text-stone-900">Find Classes</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search classes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-stone-200 text-stone-900 placeholder:text-stone-400"
              />
            </div>
            
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="h-10 px-3 rounded-md bg-white border border-stone-200 text-stone-900"
            >
              <option value="">All Cities</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="h-10 px-3 rounded-md bg-white border border-stone-200 text-stone-900"
            >
              <option value="">All Days</option>
              {DAYS_ORDER.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
            
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="h-10 px-3 rounded-md bg-white border border-stone-200 text-stone-900"
            >
              <option value="">All Levels</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="All Levels">All Levels</option>
            </select>
          </div>

          {hasFilters && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-stone-600">
                {filteredClasses.length} class{filteredClasses.length !== 1 ? 'es' : ''} found
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-violet-600 hover:text-violet-700"
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>

        {/* Classes List */}
        {filteredClasses.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No classes found"
            action={
              hasFilters ? (
                <Button
                  variant="link"
                  onClick={clearFilters}
                  className="text-violet-600"
                >
                  Clear filters to see all classes
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedClasses).map(([day, dayClasses]) => (
              <div key={day}>
                <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-violet-600" />
                  {day}
                  <span className="text-sm font-normal text-stone-600 ml-2">
                    ({dayClasses.length} class{dayClasses.length !== 1 ? 'es' : ''})
                  </span>
                </h2>
                
                <div className="grid gap-4">
                  {dayClasses.map(cls => (
                    <Card 
                      key={cls.class_id} 
                      className="bg-white border-stone-200 hover:border-violet-300 transition-colors"
                    >
                      <CardContent className="p-4 md:p-6">
                        <div className="flex flex-col md:flex-row md:items-start gap-4">
                          {/* Time Badge */}
                          <div className="flex-shrink-0 w-24 text-center bg-violet-100 rounded-lg p-3">
                            <Clock className="w-5 h-5 mx-auto text-violet-700 mb-1" />
                            <div className="text-stone-900 font-semibold">{cls.start_time}</div>
                            {cls.end_time && (
                              <div className="text-xs text-stone-600">to {cls.end_time}</div>
                            )}
                          </div>

                          {/* Main Content */}
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <h3 className="text-xl font-semibold text-stone-900">{cls.name}</h3>
                              <Badge className={`${getLevelColor(cls.level)} border`}>
                                {cls.level}
                              </Badge>
                            </div>

                            <div className="mt-3 space-y-2 text-stone-700">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-violet-600 flex-shrink-0" />
                                <span>{cls.venue_name}</span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-violet-600 flex-shrink-0" />
                                <span>
                                  {cls.address && `${cls.address}, `}
                                  {cls.city}
                                  {cls.postcode && `, ${cls.postcode}`}
                                </span>
                              </div>

                              {cls.price && (
                                <div className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium mt-2">
                                  {cls.price}
                                </div>
                              )}
                            </div>

                            {cls.description && (
                              <p className="mt-3 text-stone-600 text-sm line-clamp-2">
                                {cls.description}
                              </p>
                            )}

                            {/* Contact Info */}
                            {(cls.contact_name || cls.contact_email || cls.contact_phone || cls.website) && (
                              <div className="mt-4 pt-4 border-t border-stone-200 flex flex-wrap gap-4 text-sm">
                                {cls.contact_name && (
                                  <span className="text-stone-700">{cls.contact_name}</span>
                                )}
                                {cls.contact_email && (
                                  <a 
                                    href={`mailto:${cls.contact_email}`}
                                    className="flex items-center gap-1 text-violet-600 hover:text-violet-700"
                                  >
                                    <Mail className="w-4 h-4" />
                                    Email
                                  </a>
                                )}
                                {cls.contact_phone && (
                                  <a 
                                    href={`tel:${cls.contact_phone}`}
                                    className="flex items-center gap-1 text-violet-600 hover:text-violet-700"
                                  >
                                    <Phone className="w-4 h-4" />
                                    {cls.contact_phone}
                                  </a>
                                )}
                                {cls.website && (
                                  <a 
                                    href={cls.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-violet-600 hover:text-violet-700"
                                  >
                                    <Globe className="w-4 h-4" />
                                    Website
                                  </a>
                                )}
                              </div>
                            )}

                            {/* Links to Club/Instructor */}
                            {(cls.club_name || cls.instructor_name) && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {cls.club_name && cls.club && (
                                  <Link 
                                    to={`/clubs/${cls.club.slug}`}
                                    className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded hover:bg-violet-200 transition-colors flex items-center gap-1"
                                  >
                                    Club: {cls.club_name}
                                    <ChevronRight className="w-3 h-3" />
                                  </Link>
                                )}
                                {cls.instructor_name && cls.instructor && (
                                  <Link 
                                    to={`/instructors/${cls.instructor.instructor_id}`}
                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors flex items-center gap-1"
                                  >
                                    Instructor: {cls.instructor_name}
                                    <ChevronRight className="w-3 h-3" />
                                  </Link>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
